import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useState } from 'react';
import { apiAxios } from '../../api/axios';
import { borderStyle, perelynBlack, perelynPrimary } from '../../styles/consts';

interface EmailJobDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (jobId: string) => void;
}

export default function EmailJobDialog({ open, onClose, onSuccess }: EmailJobDialogProps) {
  const [jobTitle, setJobTitle] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!jobDescription || jobDescription.length < 50) {
      setError('Please provide a project description with at least 50 characters');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiAxios.post('/email-job/ingest', {
        job_title: jobTitle,
        contact_person: contactPerson,
        contact_email: contactEmail,
        contact_phone: contactPhone,
        job_description: jobDescription
      });

      const data = response.data;

      setSuccess(`Project successfully queued! Job ID: ${data.job_id}`);
      
      // Clear form after successful submission but don't auto-close
      setJobTitle('');
      setContactPerson('');
      setContactEmail('');
      setContactPhone('');
      setJobDescription('');
      if (onSuccess) {
        onSuccess(data.job_id);
      }

    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setJobTitle('');
      setContactPerson('');
      setContactEmail('');
      setContactPhone('');
      setJobDescription('');
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          minHeight: '500px',
          border: borderStyle,
          borderRadius: 0,
          boxShadow: 'none'
        }
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6">Import Project</Typography>
        </Stack>
      </DialogTitle>
      
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success">
              {success}
            </Alert>
          )}

          <Stack spacing={0.5}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Project Title (Optional)
            </Typography>
            <TextField
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              fullWidth
              placeholder="e.g., Python Developer Position - Berlin"
              disabled={loading}
              size="small"
            />
          </Stack>

          <Stack spacing={0.5}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Contact Person (Optional)
            </Typography>
            <TextField
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
              fullWidth
              placeholder="e.g., John Doe"
              disabled={loading}
              size="small"
            />
          </Stack>

          <Stack spacing={0.5}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Contact Information (Optional)
            </Typography>
            <Stack spacing={1}>
              <TextField
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                fullWidth
                placeholder="Email: recruiter@company.com"
                type="email"
                disabled={loading}
                size="small"
              />
              <TextField
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                fullWidth
                placeholder="Phone: +49 123 456789"
                disabled={loading}
                size="small"
              />
            </Stack>
          </Stack>

          <Stack spacing={0.5}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Project Description *
            </Typography>
            <TextField
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              multiline
              rows={10}
              fullWidth
              required
              placeholder="Paste the complete project description here. Include all details like skills, requirements, duration, location, etc."
              disabled={loading}
              helperText={`${jobDescription.length} characters (minimum 50 required)`}
              error={jobDescription.length > 0 && jobDescription.length < 50}
            />
          </Stack>

          <Typography variant="caption" color="text.secondary">
            The project description will be automatically parsed to extract relevant details like:
            duration, start date, workload, skills, hourly rate, and more.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          variant="outlined"
          sx={{
            border: borderStyle,
            backgroundColor: 'transparent',
            color: perelynBlack,
            '&:hover': {
              backgroundColor: perelynPrimary,
              border: borderStyle,
            },
            '&:disabled': {
              opacity: 0.5
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="primary"
          disabled={loading || jobDescription.length < 50}
          startIcon={loading ? <CircularProgress size={20} sx={{ color: perelynBlack }} /> : undefined}
        >
          {loading ? 'Processing...' : 'Import Project'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}